
import React, { useState, useCallback, useEffect } from 'react';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { PlusCircle, Search, User, Users, Loader2 } from 'lucide-react';
import { cn } from "@/lib/utils";
import CustomerForm from '@/components/customers/CustomerForm';
import { toast } from 'sonner';
import { createCustomer, searchCustomers } from '@/services/customerService';
import { useAuth } from '@/hooks/useAuth';
import { Customer } from '@/lib/data';
import { useDebounce } from '@/hooks/useDebounce';

interface CustomerSectionProps {
  form: any;
  customers: any[];
  isLoading: boolean;
  isEditMode: boolean;
  disableNewCustomer: boolean;
  refetchCustomers: () => Promise<any>;
}

const CustomerSection: React.FC<CustomerSectionProps> = ({
  form,
  customers,
  isLoading: initialLoading,
  isEditMode,
  disableNewCustomer,
  refetchCustomers
}) => {
  const [customerMode, setCustomerMode] = useState<'new' | 'existing'>(
    disableNewCustomer ? 'existing' : (form.getValues('customer_id') ? 'existing' : 'new')
  );
  const [isCreatingCustomer, setIsCreatingCustomer] = useState(false);
  const [customerSearchOpen, setCustomerSearchOpen] = useState(false);
  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const { user } = useAuth();
  
  const selectedCustomerId = form.getValues('customer_id');
  const selectedCustomer = customers.find(c => c.id === selectedCustomerId) || 
                          searchResults.find(c => c.id === selectedCustomerId);
  
  // Debounce search input to avoid excessive API calls
  const debouncedSearchTerm = useDebounce(customerSearchInput, 300);

  // Effect to handle backend search when user types at least 3 characters
  useEffect(() => {
    async function performSearch() {
      if (debouncedSearchTerm.length < 3) {
        setSearchResults(customers.slice(0, 10)); // Show first 10 customers by default
        return;
      }
      
      try {
        setIsSearching(true);
        const results = await searchCustomers(debouncedSearchTerm);
        console.log(`Search results for "${debouncedSearchTerm}":`, results.length);
        setSearchResults(results);
      } catch (error) {
        console.error('Error searching customers:', error);
      } finally {
        setIsSearching(false);
      }
    }

    performSearch();
  }, [debouncedSearchTerm, customers]);
  
  // Initialize with some customers
  useEffect(() => {
    if (customers.length > 0) {
      setSearchResults(customers.slice(0, 10)); // Show first 10 customers initially
    }
  }, [customers]);
  
  const handleCustomerCreated = useCallback(async (data: any) => {
    if (!user) {
      toast.error('You must be logged in to create a customer');
      return;
    }

    setIsCreatingCustomer(true);
    try {
      const customerData = {
        customer_name: data.customer_name,
        customer_number: data.customer_number,
        customer_type: data.customer_type,
        phone: data.phone,
        email: data.email,
        organisation_number: data.organisation_number,
        contact_firstname: data.contact_firstname,
        contact_lastname: data.contact_lastname,
        first_name: data.first_name,
        last_name: data.last_name,
        postal_street: data.postal_street,
        postal_zip_code: data.postal_zip_code,
        postal_city: data.postal_city
      };
      
      const newCustomer = await createCustomer(customerData);
      
      await refetchCustomers();
      form.setValue('customer_id', newCustomer.id);
      setCustomerMode('existing');
    } catch (error) {
      console.error('Error creating customer:', error);
      toast.error('Failed to create customer');
    } finally {
      setIsCreatingCustomer(false);
    }
  }, [user, form, refetchCustomers]);
  
  const handleCustomerSelect = useCallback((customerId: string) => {
    console.log("Customer selected:", customerId);
    form.setValue("customer_id", customerId);
    form.trigger("customer_id");
    setCustomerSearchOpen(false);
  }, [form]);

  const handleSearchInputChange = (value: string) => {
    setCustomerSearchInput(value);
  };
  
  return (
    <Card>
      <CardHeader>
        <CardTitle>Customer Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!isEditMode && !disableNewCustomer && (
          <div className="mb-4">
            <RadioGroup 
              defaultValue={customerMode} 
              onValueChange={(value) => setCustomerMode(value as 'new' | 'existing')}
              className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="new" id="new-customer" />
                <label htmlFor="new-customer" className="flex items-center cursor-pointer">
                  <PlusCircle className="h-4 w-4 mr-2" />
                  <span>New Customer</span>
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="existing" id="existing-customer" />
                <label htmlFor="existing-customer" className="flex items-center cursor-pointer">
                  <Users className="h-4 w-4 mr-2" />
                  <span>Existing Customer</span>
                </label>
              </div>
            </RadioGroup>
          </div>
        )}

        {!isEditMode && !disableNewCustomer && customerMode === 'new' && (
          <div className="border rounded-md p-4 bg-muted/20">
            <CustomerForm 
              onSubmit={handleCustomerCreated} 
              isLoading={isCreatingCustomer} 
              submitButtonText="" 
              id="customer-form"
            />
          </div>
        )}

        {(isEditMode || customerMode === 'existing' || disableNewCustomer) && (
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="customer_id"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel>Select Customer</FormLabel>
                  <Popover 
                    open={customerSearchOpen} 
                    onOpenChange={setCustomerSearchOpen}
                  >
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={customerSearchOpen}
                          className={cn(
                            "w-full justify-between text-left font-normal",
                            !field.value && "text-muted-foreground"
                          )}
                          disabled={initialLoading || isEditMode}
                          type="button"
                          onClick={() => {
                            setCustomerSearchOpen(true);
                            console.log("Customer search opened");
                          }}
                        >
                          {field.value && selectedCustomer ? (
                            <span>
                              {selectedCustomer.customer_name}
                              {selectedCustomer.customer_number ? 
                                ` (${selectedCustomer.customer_number})` : ''}
                            </span>
                          ) : (
                            <span>Select a customer</span>
                          )}
                          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0" align="start">
                      <Command shouldFilter={false}>
                        <CommandInput 
                          placeholder="Type at least 3 characters to search..." 
                          value={customerSearchInput}
                          onValueChange={handleSearchInputChange}
                          autoFocus
                        />
                        <CommandList>
                          {isSearching ? (
                            <div className="flex items-center justify-center p-4">
                              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                              <span className="ml-2 text-sm text-muted-foreground">Searching...</span>
                            </div>
                          ) : (
                            <>
                              {customerSearchInput.length > 0 && customerSearchInput.length < 3 && (
                                <p className="px-2 py-3 text-xs text-center text-muted-foreground">
                                  Please type at least 3 characters to search
                                </p>
                              )}
                              <CommandEmpty>No customers found</CommandEmpty>
                              <CommandGroup>
                                {searchResults.map((customer) => (
                                  <CommandItem
                                    key={customer.id}
                                    value={customer.id}
                                    onSelect={() => handleCustomerSelect(customer.id)}
                                  >
                                    <User className="mr-2 h-4 w-4" />
                                    <span className="flex-1 truncate">
                                      {customer.customer_name}
                                      {customer.customer_number ? ` (${customer.customer_number})` : ''}
                                    </span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {!isEditMode && !disableNewCustomer && customerMode === 'existing' && (
              <div className="flex justify-center">
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setCustomerMode('new')}
                >
                  <PlusCircle className="h-4 w-4 mr-2" />
                  Create New Customer
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomerSection;
